import { useEffect, useMemo, useState } from "react"
import { DateRange, PropsRange } from "react-day-picker"
import { isBefore, isWithinInterval } from "date-fns"
import { Calendar, CalendarFooter } from "@/components/ui/calendar"
import { DateInput, DatePickerFooter } from "@/components/ui/date-picker"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { formatDateRange } from "@/lib/format-date"

type ChildrenFunction = (props: { selectedRange: DateRange | undefined; focus: boolean }) => React.ReactNode

type DateRangePickerProps = {
  label?: string
  dataTestId?: string
  onChange?: PropsRange["onSelect"]
  onReset?(): void
  onClose?(): void
  className?: string
  value?: DateRange
  shortcuts?: React.ReactNode
  children?: React.ReactElement | ChildrenFunction
  dateFormat?: string
  today?: Date
  showFooter?: boolean
  error?: string
}

export const DateRangePicker = (props: DateRangePickerProps) => {
  const {
    onChange,
    onClose,
    today = new Date(),
    onReset,
    value,
    shortcuts,
    dateFormat,
    children,
    label,
    showFooter = true,
    ...dateInputProps
  } = props

  const [hoveringDate, setHoveringDate] = useState<Date | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const handleOpenChange = (state: boolean) => {
    if (!state) {
      onClose?.()
    }
    setIsFocused(state)
    setOpen(state)
  }

  const handleCalendarIconClick = (e: React.SyntheticEvent<HTMLElement, Event>) => {
    e.preventDefault()
    setOpen((isOpen) => !isOpen)
    setIsFocused(true)
  }

  const handleSelect: PropsRange["onSelect"] = (range, selectedDate, ...rest) => {
    let selectingRange = range

    if (value?.to) {
      selectingRange = { from: selectedDate, to: undefined }
    }

    onChange?.(selectingRange, selectedDate, ...rest)
  }

  const isWithinRange = (date: Date) => {
    if (value?.from && hoveringDate) {
      const startDate = isBefore(value.from, hoveringDate) ? value.from : hoveringDate
      const endDate = isBefore(value.from, hoveringDate) ? hoveringDate : value.from
      return isWithinInterval(date, { start: startDate, end: endDate })
    }
    return false
  }

  const resetHoveringDate = () => setHoveringDate(undefined)

  useEffect(() => {
    if (!isFocused) return

    const calendarBody = document.querySelector(".js-sofia-rdp-tbody")

    if (!calendarBody) return

    calendarBody.addEventListener("mouseleave", resetHoveringDate)

    return () => {
      calendarBody.removeEventListener("mouseleave", resetHoveringDate)
    }
  }, [isFocused])

  const isFunctionChild = children && typeof children === "function"

  const isDirty = useMemo(() => value?.from || value?.to, [value])

  const components = showFooter
    ? {
        Footer: () => (
          <DatePickerFooter>
            <CalendarFooter disabled={!isDirty} onClick={onReset} />
          </DatePickerFooter>
        ),
      }
    : {}

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild className="hover:cursor-pointer">
        {isFunctionChild ? (
          (props.children as ChildrenFunction)({ selectedRange: value, focus: isFocused })
        ) : (
          <DateInput
            value={formatDateRange(value, dateFormat)}
            label={label}
            focus={isFocused}
            onCalendarIconClick={handleCalendarIconClick}
            {...dateInputProps}
          />
        )}
      </PopoverTrigger>

      <PopoverContent sideOffset={0} className="bg-surface w-auto rounded-lg p-0" align="start">
        {shortcuts && <div className="px-2 py-4">{shortcuts}</div>}
        <Calendar
          mode="range"
          today={today}
          onSelect={handleSelect}
          selected={value}
          onDayMouseEnter={setHoveringDate}
          modifiers={{
            "rdp-in-selecting": (date: Date) => {
              return isWithinRange(date) && !value?.to
            },
          }}
          components={{
            ...components,
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
